import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { selectWebsiteTheme } from "../../store/slices/website";

import {
  getCalendarEvents,
  selectCalendarEvents,
} from "../../store/slices/calendar-events";
import {
  selectTimeFormat,
} from "../../store/slices/preferences";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import CLButton from "../../components/CLButton";
import CLTabs from "../../components/CLTabs";
import CLFontIcon from "../../components/CLFontIcon";

import CalendarEventModal from "./components/CalendarEventModal";

import {
  CalendarHeader,
  CalendarHeaderNavButtons,
  CalendarHeaderViewToggle,
  CalendarHeaderTitle,
  CalendarContainer,
} from "./styled";

const calendarViews = [
  {
    label: `Month`,
    value: "dayGridMonth",
  },
  {
    label: `Week`,
    value: "timeGridWeek",
  },
  {
    label: `Day`,
    value: "timeGridDay",
  },
];

const isDateInRange = (date, start, end) => {
  return date >= start && date <= end;
};

const CalendarBoard = () => {
  const dispatch = useDispatch();

  const [calendarApi, setCalendarApi] = React.useState(null);
  const [calendarView, setCalendarView] = React.useState(
    calendarViews[0].value,
  );
  const [calendarTitle, setCalendarTitle] = React.useState("");
  const [calendarDates, setCalendarDates] = React.useState({
    start: null,
    end: null,
  });
  const [isTodayVisible, setIsTodayVisible] = React.useState(false);
  const [selectedCalendarEvent, setSelectedCalendarEvent] =
    React.useState(null);
  const { search } = window.location;
  const params = new URLSearchParams(search);
  const account_id = params.get('account_id');

  const { colors } = useSelector(selectWebsiteTheme);
  const timeFormat = useSelector(selectTimeFormat);
  const calendarEvents = useSelector(selectCalendarEvents);

  React.useEffect(() => {
    if (!calendarDates?.start || !calendarDates?.end) {
      return;
    }

    dispatch(
      getCalendarEvents({
        account_id,
        start_date: calendarDates?.start,
        end_date: calendarDates?.end,
      }),
    );
  }, [dispatch, calendarDates, account_id]);

  React.useEffect(() => {
    if (!calendarView || !calendarApi) {
      return;
    }
    calendarApi.changeView(calendarView);
  }, [calendarApi, calendarView]);

  const onSetCalendarDates = (arg) => {
    setCalendarApi(arg.view.calendar);
    setCalendarTitle(arg.view.title);
    setIsTodayVisible(
      isDateInRange(new Date(), arg.view.activeStart, arg.view.activeEnd),
    );
    setCalendarDates({
      start: arg.start?.toISOString(),
      end: arg.end?.toISOString(),
    });
  };

  const onCalendarApiToday = () => {
    calendarApi.today();
  };
  const onCalendarApiPrev = () => {
    calendarApi.prev();
  };
  const onCalendarApiNext = () => {
    calendarApi.next();
  };
  const onSetCalendarView = (tab) => {
    setCalendarView(tab.value);
  };
  const onEventClick = (arg) => {
    const event = arg.event;
    setSelectedCalendarEvent(event);
  };

  return (
    <div>
      <CalendarHeader>
        <CalendarHeaderNavButtons>
          <CLButton icon onClick={onCalendarApiPrev}>
            <CLFontIcon icon="caret-left" />
          </CLButton>
          <CLButton icon onClick={onCalendarApiNext}>
            <CLFontIcon icon="caret-right" />
          </CLButton>
          <CLButton disabled={isTodayVisible} onClick={onCalendarApiToday}>
            Today
          </CLButton>
        </CalendarHeaderNavButtons>
        <CalendarHeaderTitle>{calendarTitle}</CalendarHeaderTitle>

        <CalendarHeaderViewToggle>
          <CLTabs
            tabs={calendarViews}
            selected={calendarView}
            onChange={onSetCalendarView}
          />
        </CalendarHeaderViewToggle>
      </CalendarHeader>

      <CalendarContainer
        $primaryLight={colors[2].color}
        $primary={colors[6].color}
      >
        <FullCalendar
          events={calendarEvents}
          plugins={[dayGridPlugin, timeGridPlugin]}
          initialView={calendarView}
          height="auto"
          headerToolbar={null}
          nowIndicator={true}
          editable={false}
          slotDuration="01:00:00"
          slotMinTime="06:00"
          slotMaxTime="21:00"
          scrollTime="06:00"
          snapDuration="00:15"
          views={{
            dayGridMonth: {},
            timeGridWeek: {
              dayHeaderFormat: {
                weekday: "short",
                day: "numeric",
                omitCommas: true,
              },
            },
          }}
          slotLabelFormat={{
            hour: "numeric",
            minute: "numeric",
            hour12: timeFormat === '12-hour',
          }}
          eventTimeFormat={{
            hour: "numeric",
            minute: "numeric",
            hour12: timeFormat === '12-hour',
          }}
          datesSet={onSetCalendarDates}
          eventClick={onEventClick}
        />
      </CalendarContainer>

      <CalendarEventModal
        calendarEvent={selectedCalendarEvent}
        isVisible={!!selectedCalendarEvent}
        onClose={() => setSelectedCalendarEvent(null)}
      />
    </div>
  );
};

export default CalendarBoard;
